/**
 * Proposta de TRADE-IN (DEMO-03, telas/PropostaTradein.tsx) — o comprador
 * avalia; a INTTRIX oferece ao Cliente um valor menor. Ordem: dados da operação
 * (local pelo CNPJ do Cliente, logística estruturada, contato e agendamento) →
 * INVENTÁRIO (planilha .xlsx com prévia) → avaliações dos compradores (comparar
 * e selecionar) → VALORES por linha → dados bancários → visão interna.
 * Dados bancários (decisão de 25/09/2026, alternativa B): NÃO são guardados no
 * HUB; seguem por canal apropriado fora dele. O HUB registra só a confirmação
 * operacional ("confirmados por [usuário] em [data/hora]"), com auditoria.
 */
import { useRef, useState } from 'react';
import { api } from '../../api.ts';
import { useSessao } from '../../sessao.tsx';
import { Ajuda, Estado, Modal, Nota, ResumoIndicadores } from '../ui.tsx';
import { CampoData, CampoDataHora } from '../CampoData.tsx';
import { CampoCep, SeletorUf } from '../campos.tsx';
import { baixarArquivo } from '../Produtos.tsx';
import { dataBr } from '../../../../backend/src/compartilhado/oportunidades.ts';
import { lerXlsx } from '../../../../backend/src/compartilhado/planilha.ts';
import { COLUNAS_INVENTARIO, LIMITES_INVENTARIO, assinatura, gerarModeloInventario, validarPlanilhaInventario, type Previa } from '../../../../backend/src/compartilhado/inventario.ts';
import {
  MODALIDADES_LOGISTICA,
  RESPONSAVEIS_LOGISTICA,
  RESPONSAVEIS_POR_MODALIDADE,
  RESULTADO_LINHA,
  calcularTradein,
  calculoTradein,
  comNome,
  entidadeDe,
  grupoDe,
  liquidaEstimada,
  abatimentoEfetivo,
  localParqueDaVersao,
  nomeComponente,
  type AvaliacaoTradein,
  type ComponenteOperacao,
  type DadosTradein,
  type Endereco,
  type ModalidadeLogistica,
  type ResponsavelLogistica,
  type Versao,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { agoraSaoPaulo, useProposta, type DadosProposta } from './contexto.tsx';
import { moeda2, pctNum } from './formatos.ts';

let seq = 0;
const ENDERECO_VAZIO: Endereco = { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' };
const novoEquipamento = (extra: Partial<ComponenteOperacao> = {}): ComponenteOperacao => ({ id: `e${Date.now() % 100000}-${++seq}`, descricao: '', categoria: '', fabricante: '', modelo: '', quantidade: 1, condicao: '', ano: '', lote: '', pn: '', serial: '', processador: '', hd: '', storage: '', memoria: '', tela: '', funcional: '', acessorios: '', observacoes: '', resultado: 'pendente', quantidadeAceita: 0, ...extra });

const COLUNA_CAMPO: [string, keyof ComponenteOperacao, boolean?][] = [
  ['Categoria *', 'categoria', true],
  ['Fabricante *', 'fabricante', true],
  ['Modelo *', 'modelo', true],
  ['Part number', 'pn'],
  ['Serial number', 'serial'],
  ['Processador', 'processador'],
  ['HD', 'hd'],
  ['Storage', 'storage'],
  ['Memória', 'memoria'],
  ['Tela', 'tela'],
  ['Funcional', 'funcional'],
  ['Acessórios', 'acessorios'],
  ['Observações', 'observacoes'],
];

/**
 * Revisão 12/10 (P2-26/P2-30 também no Trade-in): `parte="resumo"` desenha só a Visão interna (no topo, como o Resumo
 * econômico da Venda); `parte="corpo"` desenha o restante. "Comparar" fica no painel único de consultas (`comparar`).
 */
export function EditorTradein({ v, editavel, set, propostaId, alterado, parte = 'corpo', comparar = false }: { v: Versao; editavel: boolean; set: (p: Partial<Versao>) => void; propostaId: string; alterado?: boolean; parte?: 'resumo' | 'corpo'; comparar?: boolean }) {
  const { d, dados, eu } = useProposta();
  const hoje = dados.opcoes.hoje;
  const tri = v.tradein!;
  const setTri = (patch: Partial<DadosTradein>) => set({ tradein: { ...tri, ...patch } });
  const setAv = (id: string, patch: Partial<AvaliacaoTradein>) => setTri({ avaliacoes: tri.avaliacoes.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
  const setEq = (id: string, patch: Partial<ComponenteOperacao>) => setTri({ equipamentos: tri.equipamentos.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
  const cliente = d.clientes[0];
  const entidadesCliente = cliente.entidades.filter((e) => e.situacao === 'Ativa');
  const compradores = d.grupos.filter((g) => g.situacao === 'Ativo' && g.papeis.includes('Comprador de Trade-in'));
  const c = calculoTradein(v);
  const sel = tri.avaliacoes.find((a) => a.id === tri.selecionadaId);
  const [nova, setNova] = useState(false);
  const [emEdicao, setEmEdicao] = useState(tri.selecionadaId);
  const [importar, setImportar] = useState(false);
  const aval = tri.avaliacoes.find((a) => a.id === emEdicao) ?? sel ?? tri.avaliacoes[0];
  const calcAval = aval ? calcularTradein(aval, comNome(tri.equipamentos)) : undefined;
  const setPreco = (linhaId: string, patch: Partial<{ unitBruto: number; unitCliente: number }>) => aval && setAv(aval.id, { linhas: { ...aval.linhas, [linhaId]: { ...{ unitBruto: 0, unitCliente: 0 }, ...aval.linhas[linhaId], ...patch } } });
  const entSel = sel ? entidadeDe(d, sel.entidadeId) : undefined;
  const local = localParqueDaVersao(d, v);
  const mod = tri.modalidadeLogistica || '';
  const responsaveisPermitidos = mod ? RESPONSAVEIS_POR_MODALIDADE[mod] : [...RESPONSAVEIS_LOGISTICA];
  const abat = abatimentoEfetivo(v.abatimento, dados.opcoes.abatimento); // null = não configurado
  if (parte === 'resumo')
    return (
      <section className="resumo-economico resumo-destaque" data-teste="resumo-tradein" aria-label="Visão interna do Trade-in">
        <h2>
          Visão interna do Trade-in 🔒
          <Ajuda titulo="Visão interna do Trade-in">Valor a receber pelo Cliente na alienação dos equipamentos (não é venda de produtos pela Inttrix). Avaliação bruta, comissão, percentual e avaliações não selecionadas NÃO aparecem no PDF do Cliente.</Ajuda>
        </h2>
        {c && sel && entSel ? (
          <ResumoIndicadores
            itens={[
              { rotulo: 'Avaliação bruta total do comprador', valor: moeda2(c.bruto) },
              { rotulo: 'Valor total destinado ao Cliente', valor: moeda2(c.oferta) },
              { rotulo: 'Comissão bruta Inttrix', valor: moeda2(c.comissao), detalhe: `${pctNum(c.percentualEquivalente)} da operação` },
              { rotulo: abat ? `Comissão líquida estimada (abatimento ${pctNum(abat.percentual)})` : 'Comissão líquida estimada', valor: <span data-teste="liquida-tradein">{abat ? moeda2(liquidaEstimada(c.comissao, abat.percentual)) : 'abatimento não configurado'}</span> },

            ]}
            secundarios={[
              { rotulo: 'Parceiro de Trade-in selecionado', valor: `${entSel.razao} — ${entSel.cnpj}` },
              { rotulo: 'Percentual equivalente da operação', valor: pctNum(c.percentualEquivalente) },
              { rotulo: 'Situação do cálculo', valor: c.erros.length ? `bloqueado: ${c.erros.join(' ')}` : 'consistente (valor ao Cliente + comissão = avaliação)' },
              { rotulo: 'Dados bancários do favorecido', valor: tri.confirmacaoBancaria ? `confirmados por ${tri.confirmacaoBancaria.usuario} (fora do HUB)` : 'NÃO confirmados — a versão não pode ser enviada' },
            ]}
          />
        ) : (
          <Estado tipo="vazio" titulo="nenhum parceiro selecionado" />
        )}
      </section>
    );
  return (
    <>
      <fieldset className="form-grade" disabled={!editavel} data-teste="secao-tradein">
        <legend>Dados da operação de Trade-in</legend>
        <label className="coluna-dupla">
          Local do parque
          <Ajuda titulo="Local do parque">Vem do CNPJ do Cliente: com um endereço, é preenchido; com vários, escolha. “Outro local” vale só para esta operação e não altera o endereço oficial.</Ajuda>
          <select value={tri.localEntidadeId ?? ''} onChange={(e) => setTri({ localEntidadeId: e.target.value || undefined, localOutro: e.target.value === 'outro' ? (tri.localOutro ?? { ...ENDERECO_VAZIO }) : tri.localOutro })} data-teste="local-parque">
            <option value="">Selecione…</option>
            {entidadesCliente.map((e) => (
              <option key={e.id} value={e.id}>
                {e.tipo}: {e.razao} — {e.cnpj}
              </option>
            ))}
            <option value="outro">Outro local (só nesta operação)</option>
          </select>
        </label>
        <p className="largura-total texto-secundario sem-margem" data-teste="endereco-parque">
          {local.texto} · <em>{local.origem}</em>
        </p>
        {tri.localEntidadeId === 'outro' && (
          <>
            <CampoCep valor={tri.localOutro?.cep ?? ''} aoMudar={(cep) => setTri({ localOutro: { ...(tri.localOutro ?? ENDERECO_VAZIO), cep } })} teste="cep-outro-local" />
            {(['logradouro', 'numero', 'complemento', 'bairro', 'cidade'] as const).map((k) => (
              <label key={k} className={k === 'logradouro' ? 'coluna-dupla' : undefined}>
                {k === 'numero' ? 'Número' : k[0].toUpperCase() + k.slice(1)}
                <input value={tri.localOutro?.[k] ?? ''} onChange={(e) => setTri({ localOutro: { ...(tri.localOutro ?? ENDERECO_VAZIO), [k]: e.target.value } })} data-teste={`outro-${k}`} />
              </label>
            ))}
            <SeletorUf valor={tri.localOutro?.uf ?? ''} aoMudar={(uf) => setTri({ localOutro: { ...(tri.localOutro ?? ENDERECO_VAZIO), uf } })} teste="uf-outro-local" />
          </>
        )}
        <label>
          Modalidade da logística
          <select
            value={mod}
            onChange={(e) => {
              const m = e.target.value as ModalidadeLogistica | '';
              const permitidos = m ? RESPONSAVEIS_POR_MODALIDADE[m] : [...RESPONSAVEIS_LOGISTICA];
              // Entrega pelo Cliente SUGERE o Cliente como responsável; responsável incompatível volta a "A definir".
              const resp: ResponsavelLogistica = m === 'Entrega pelo Cliente' ? 'Cliente' : tri.responsavelLogistica && permitidos.includes(tri.responsavelLogistica) ? tri.responsavelLogistica : 'A definir';
              setTri({ modalidadeLogistica: m, responsavelLogistica: resp });
            }}
            data-teste="modalidade-logistica"
          >
            <option value="">Selecione…</option>
            {MODALIDADES_LOGISTICA.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label>
          Responsável pela logística
          <select value={tri.responsavelLogistica ?? 'A definir'} onChange={(e) => setTri({ responsavelLogistica: e.target.value as ResponsavelLogistica })} data-teste="responsavel-logistica">
            {responsaveisPermitidos.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <CampoData rotulo="Prazo para retirada" valor={tri.prazoRetirada ?? ''} aoMudar={(x) => setTri({ prazoRetirada: x })} min={hoje} teste="prazo-retirada" desabilitado={!editavel} />
        <CampoData rotulo="Validade da avaliação" valor={tri.validadeAvaliacao} aoMudar={(x) => setTri({ validadeAvaliacao: x })} teste="validade-avaliacao" desabilitado={!editavel} />
        <div className="coluna-dupla">
          <CampoDataHora rotulo="Agendamento" valor={tri.agendamento ?? ''} aoMudar={(x) => setTri({ agendamento: x })} teste="agendamento" />
        </div>
        <div className="subtitulo-form largura-total">Contato responsável no local</div>
        {(['nome', 'telefone', 'email'] as const).map((k) => (
          <label key={k}>
            {k === 'nome' ? 'Nome' : k === 'telefone' ? 'Telefone' : 'E-mail'}
            <input value={tri.contatoLocal?.[k] ?? ''} onChange={(e) => setTri({ contatoLocal: { ...(tri.contatoLocal ?? { nome: '', telefone: '', email: '' }), [k]: e.target.value } })} data-teste={`contato-local-${k}`} />
          </label>
        ))}
        <label className="largura-total">
          Responsabilidades e preparação
          <textarea rows={2} value={tri.preparacao || tri.responsabilidades} onChange={(e) => setTri({ preparacao: e.target.value, responsabilidades: e.target.value })} data-teste="preparacao" />
        </label>
        <label className="largura-total">
          Condições de inspeção e aceite
          <textarea rows={2} value={tri.condicoesOperacao} onChange={(e) => setTri({ condicoesOperacao: e.target.value })} data-teste="inspecao-aceite" />
        </label>
        <label className="largura-total">
          Observações
          <input value={tri.observacoes} onChange={(e) => setTri({ observacoes: e.target.value })} />
        </label>
        {mod && tri.responsavelLogistica === 'A definir' && (
          <p className="aviso aviso-alerta largura-total" data-teste="logistica-a-definir">
            <span className="rotulo-nivel">▲ Atenção</span>Responsável pela logística “A definir”: aceito no rascunho, mas precisa ser resolvido antes do envio definitivo.
          </p>
        )}
      </fieldset>

      <section className="secao-editor" data-teste="inventario">
        <div className="barra-titulo">
          <h2>
            Inventário
            <Ajuda titulo="Inventário">Categoria, Fabricante e Modelo são obrigatórios. Quantidade começa em 1: equipamento com serial = 1 unidade; lote sem serial = quantidade do lote. Os valores ficam na seção seguinte.</Ajuda>
          </h2>
          {editavel && (
            <div className="acoes-direita">
              <button type="button" className="botao-secundario" onClick={() => baixarArquivo(gerarModeloInventario(), 'modelo-inventario-trade-in.xlsx')} data-teste="baixar-modelo">
                Baixar modelo (.xlsx)
              </button>
              <button type="button" className="botao-secundario" onClick={() => setImportar(true)} data-teste="importar-planilha">
                Importar planilha
              </button>
              <button type="button" className="botao-primario" onClick={() => setTri({ equipamentos: [...tri.equipamentos, novoEquipamento()] })} data-teste="adicionar-equipamento">
                Adicionar equipamento
              </button>
            </div>
          )}
        </div>
        <div className="rolagem">
          <table className="tabela tabela-compacta tabela-editor tabela-inventario" data-teste="equipamentos">
            <thead>
              <tr>
                <th>#</th>
                {COLUNA_CAMPO.map(([rot]) => (
                  <th key={rot}>{rot}</th>
                ))}
                <th>Quantidade</th>
                <th>Resultado</th>
                {editavel && <th className="col-acoes">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {tri.equipamentos.map((e, n) => (
                <tr key={e.id} data-teste={`equipamento-${n + 1}`}>
                  <td>{n + 1}</td>
                  {COLUNA_CAMPO.map(([rot, k, obrig]) => (
                    <td key={k}>
                      <input value={String(e[k] ?? '')} disabled={!editavel} onChange={(ev) => setEq(e.id, { [k]: ev.target.value })} aria-label={`${rot.replace(' *', '')} da linha ${n + 1}`} aria-invalid={obrig ? !String(e[k] ?? '').trim() : undefined} className={k === 'observacoes' || k === 'modelo' ? 'entrada-media' : 'entrada-curta-media'} data-teste={`eq-${k}-${n + 1}`} />
                    </td>
                  ))}
                  <td>
                    <input type="number" min={1} step={1} value={e.quantidade} disabled={!editavel} onChange={(ev) => setEq(e.id, { quantidade: Number(ev.target.value) })} aria-label={`Quantidade da linha ${n + 1}`} aria-invalid={!(Number.isInteger(e.quantidade) && e.quantidade >= 1)} className="entrada-curta" data-teste={`eq-quantidade-${n + 1}`} />
                  </td>
                  <td>{RESULTADO_LINHA[e.resultado]}</td>
                  {editavel && (
                    <td className="col-acoes">
                      <button type="button" className="botao-secundario pequeno" disabled={tri.equipamentos.length === 1} onClick={() => setTri({ equipamentos: tri.equipamentos.filter((x) => x.id !== e.id) })}>
                        Remover
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(tri.importacoes ?? []).length > 0 && (
          <details className="historico-importacoes" data-teste="historico-importacoes">
            <summary>Importações registradas ({tri.importacoes!.length})</summary>
            <ul className="lista-simples">
              {tri.importacoes!.map((i) => (
                <li key={i.assinatura}>
                  {i.arquivo} — {i.usuario} em {dataBr(i.dataHora.slice(0, 10))} às {i.dataHora.slice(11, 16)}: {i.validas} válida(s) importada(s), {i.invalidas} inválida(s), {i.duplicadas} duplicada(s), {i.ignoradas} ignorada(s)
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <section className="secao-editor" data-teste="cotacoes">
        <div className="barra-titulo">
          <h2>
            Avaliações dos parceiros compradores
            <Ajuda titulo="Avaliações">1. Adicionar avaliação; 2. escolher o Parceiro comercial classificado como Comprador de Trade-in; 3. a entidade/CNPJ compradora; 4. informar a avaliação por linha; 5. comparar; 6. marcar uma como Selecionada.</Ajuda>
          </h2>
          <div className="acoes-direita">
            {editavel && (
              <button type="button" className="botao-secundario" disabled={!compradores.length} title={!compradores.length ? 'Cadastre um parceiro com o papel Comprador de Trade-in' : undefined} onClick={() => setNova(true)} data-teste="adicionar-avaliacao">
                Adicionar avaliação
              </button>
            )}
          </div>
        </div>
        <p className={`parceiro-selecionado${entSel ? '' : ' pendente'}`} data-teste="parceiro-tradein-selecionado">
          {entSel ? (
            <>
              Parceiro de Trade-in selecionado: <strong>{entSel.razao}</strong> — {entSel.cnpj}
            </>
          ) : (
            'Nenhum parceiro de Trade-in selecionado: o rascunho pode ser salvo, mas o envio fica bloqueado.'
          )}
        </p>
        <div className="rolagem">
          <table className="tabela tabela-compacta tabela-editor" data-teste="tabela-avaliacoes">
            <thead>
              <tr>
                <th>Parceiro comprador · entidade/CNPJ</th>
                <th>Modo da comissão</th>
                <th>% comissão</th>
                <th>Avaliação bruta total</th>
                <th>Total ao Cliente</th>
                <th>Comissão Inttrix</th>
                <th>% equivalente</th>
                <th>Prazo de retirada (dias)</th>
                <th>Validade (dias)</th>
                <th>Condições</th>
                <th>Selecionada</th>
                {editavel && <th className="col-acoes">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {tri.avaliacoes.map((a, k) => {
                const x = calcularTradein(a, comNome(tri.equipamentos));
                const maior = Math.max(...tri.avaliacoes.map((y) => calcularTradein(y, comNome(tri.equipamentos)).oferta));
                const e = entidadeDe(d, a.entidadeId);
                return (
                  <tr key={a.id} className={a.id === tri.selecionadaId ? 'linha-escolhida' : ''} data-teste={`avaliacao-${k + 1}`}>
                    <td>
                      {grupoDe(d, a.entidadeId)?.nome ?? '—'}
                      <div className="texto-secundario">
                        {e?.razao} — <span className="nowrap">{e?.cnpj}</span>
                      </div>
                    </td>
                    <td>
                      <select value={a.modo} disabled={!editavel} onChange={(ev) => setAv(a.id, { modo: ev.target.value as AvaliacaoTradein['modo'] })} aria-label={`Modo da comissão da avaliação ${k + 1}`} data-teste={`modo-avaliacao-${k + 1}`}>
                        <option value="percentual">Percentual</option>
                        <option value="valor">Valor por linha (diferença)</option>
                      </select>
                    </td>
                    <td>
                      <input type="number" min={0} max={99} step="0.1" value={a.percentual} disabled={!editavel || a.modo !== 'percentual'} onChange={(ev) => setAv(a.id, { percentual: Number(ev.target.value) })} aria-label={`Percentual de comissão da avaliação ${k + 1}`} className="entrada-curta" data-teste={`percentual-avaliacao-${k + 1}`} />
                    </td>
                    <td className="nowrap">{moeda2(x.bruto)}</td>
                    <td className="nowrap">
                      {moeda2(x.oferta)}
                      {comparar && x.oferta === maior && <div className="diferenca-positiva">maior valor ao Cliente</div>}
                    </td>
                    <td className={`nowrap${x.comissao < 0 ? ' diferenca-negativa' : ''}`}>{moeda2(x.comissao)}</td>
                    <td>{pctNum(x.percentualEquivalente)}</td>
                    <td>
                      <input type="number" min={0} value={a.prazoRetiradaDias} disabled={!editavel} onChange={(ev) => setAv(a.id, { prazoRetiradaDias: Math.max(0, Math.floor(Number(ev.target.value)) || 0) })} aria-label={`Prazo de retirada da avaliação ${k + 1}`} className="entrada-curta" />
                    </td>
                    <td>
                      <input type="number" min={0} value={a.validadeDias} disabled={!editavel} onChange={(ev) => setAv(a.id, { validadeDias: Math.max(0, Math.floor(Number(ev.target.value)) || 0) })} aria-label={`Validade da avaliação ${k + 1}`} className="entrada-curta" />
                    </td>
                    <td>
                      <input value={a.condicoes} disabled={!editavel} onChange={(ev) => setAv(a.id, { condicoes: ev.target.value })} aria-label={`Condições da avaliação ${k + 1}`} className="entrada-media" />
                    </td>
                    <td>
                      <label className="opcao">
                        <input type="radio" name="avaliacao-sel" checked={a.id === tri.selecionadaId} disabled={!editavel} onChange={() => (setTri({ selecionadaId: a.id }), setEmEdicao(a.id))} aria-label={`Selecionar a avaliação ${k + 1}`} data-teste={`selecionar-avaliacao-${k + 1}`} />
                        {a.id === tri.selecionadaId ? 'Selecionada' : 'Selecionar'}
                      </label>
                      {x.erros.length > 0 && (
                        <div className="diferenca-negativa" data-teste={`erro-avaliacao-${k + 1}`}>
                          {x.erros[0]}
                        </div>
                      )}
                    </td>
                    {editavel && (
                      <td className="col-acoes">
                        <button type="button" className="botao-secundario pequeno" onClick={() => setEmEdicao(a.id)}>
                          Editar valores
                        </button>{' '}
                        <button type="button" className="botao-secundario pequeno" onClick={() => setTri({ avaliacoes: tri.avaliacoes.filter((y) => y.id !== a.id), selecionadaId: tri.selecionadaId === a.id ? '' : tri.selecionadaId })}>
                          Remover
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {tri.avaliacoes.length === 0 && (
                <tr>
                  <td colSpan={12}>
                    <Estado tipo="vazio" titulo="nenhuma avaliação — use Adicionar avaliação" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="secao-editor" data-teste="valores-linha">
        <div className="barra-titulo">
          <h2>
            Valores por linha
            <Ajuda titulo="Valores por linha">Avaliação bruta = quantidade × valor unitário do comprador; total ao Cliente = quantidade × valor unitário destinado ao Cliente. Lote fechado: quantidade 1 com o valor do lote como unitário.</Ajuda>
          </h2>
          {tri.avaliacoes.length > 0 && (
            <label className="acoes-direita">
              Valores da avaliação de{' '}
              <select value={aval?.id} onChange={(e) => setEmEdicao(e.target.value)} data-teste="avaliacao-em-edicao">
                {tri.avaliacoes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {entidadeDe(d, a.entidadeId)?.fantasia}
                    {a.id === tri.selecionadaId ? ' (selecionada)' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {aval ? (
          <div className="rolagem">
            <table className="tabela tabela-compacta tabela-editor" data-teste="tabela-valores">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Equipamento</th>
                  <th>Qtd</th>
                  <th>Valor unitário avaliado (comprador)</th>
                  <th>Avaliação bruta total</th>
                  <th>Valor unitário ao Cliente</th>
                  <th>Total ao Cliente</th>
                  <th>Comissão da linha</th>
                  <th>% equivalente</th>
                </tr>
              </thead>
              <tbody>
                {tri.equipamentos.map((e, n) => {
                  const l = calcAval?.linhas.find((x) => x.id === e.id);
                  const p = aval.linhas[e.id];
                  return (
                    <tr key={e.id} data-teste={`valor-linha-${n + 1}`}>
                      <td>{n + 1}</td>
                      <td>
                        {nomeComponente(e)}
                        {e.serial && <div className="texto-secundario">{e.serial}</div>}
                      </td>
                      <td>{e.quantidade}</td>
                      <td>
                        <input type="number" min={0} step="0.01" value={p?.unitBruto ?? 0} disabled={!editavel} onChange={(ev) => setPreco(e.id, { unitBruto: Number(ev.target.value) })} aria-label={`Valor unitário avaliado da linha ${n + 1}`} className="entrada-media" data-teste={`bruto-unit-${n + 1}`} />
                      </td>
                      <td className="nowrap" data-teste={`bruto-linha-${n + 1}`}>
                        {moeda2(l?.totalBruto ?? 0)}
                      </td>
                      <td>
                        <input type="number" min={0} step="0.01" value={aval.modo === 'percentual' ? (l?.unitCliente ?? 0) : (p?.unitCliente ?? 0)} disabled={!editavel || aval.modo === 'percentual'} onChange={(ev) => setPreco(e.id, { unitCliente: Number(ev.target.value) })} aria-label={`Valor unitário ao Cliente da linha ${n + 1}`} className="entrada-media" data-teste={`cliente-unit-${n + 1}`} />
                      </td>
                      <td className="nowrap" data-teste={`cliente-linha-${n + 1}`}>
                        {moeda2(l?.totalCliente ?? 0)}
                      </td>
                      <td className={`nowrap${(l?.comissao ?? 0) < 0 ? ' diferenca-negativa' : ''}`} data-teste={`comissao-linha-${n + 1}`}>
                        {moeda2(l?.comissao ?? 0)}
                      </td>
                      <td>{pctNum(l?.percentual ?? 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {calcAval && (
                <tfoot>
                  <tr data-teste="total-equipamentos">
                    <th colSpan={4}>Consolidado ({entidadeDe(d, aval.entidadeId)?.fantasia})</th>
                    <th className="nowrap">{moeda2(calcAval.bruto)}</th>
                    <th />
                    <th className="nowrap">{moeda2(calcAval.oferta)}</th>
                    <th className="nowrap">{moeda2(calcAval.comissao)}</th>
                    <th>{pctNum(calcAval.percentualEquivalente)}</th>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <Estado tipo="bloqueado" titulo="Selecione um parceiro de Trade-in (Adicionar avaliação) para informar os valores" />
        )}
        {calcAval && calcAval.erros.length > 0 && <Estado tipo="erro" titulo="corrija os valores" itens={calcAval.erros} teste="erros-tradein" />}
      </section>

      <ConfirmacaoBancariaTradein v={v} editavel={editavel} propostaId={propostaId} alterado={alterado} />

      {nova && (
        <ModalNovaAvaliacao
          compradores={compradores.map((g) => ({ g, ents: d.entidades.filter((e) => e.grupoId === g.id && e.situacao === 'Ativa') }))}
          aoFechar={() => setNova(false)}
          aoCriar={(entidadeId) => {
            const id = `av${Date.now() % 100000}-${++seq}`;
            setTri({ avaliacoes: [...tri.avaliacoes, { id, entidadeId, modo: 'valor', percentual: 0, linhas: {}, prazoRetiradaDias: 15, logistica: '', validadeDias: 15, condicoes: '' }] });
            setEmEdicao(id);
          }}
        />
      )}
      {importar && <ModalImportarPlanilha tri={tri} eu={eu} aoFechar={() => setImportar(false)} aoConfirmar={(novos, registro) => setTri({ equipamentos: [...tri.equipamentos, ...novos], importacoes: [...(tri.importacoes ?? []), registro] })} />}
      <Nota>Somente a avaliação selecionada alimenta cálculos e documentos. Envio bloqueado sem parceiro selecionado, equipamentos, valores ao Cliente, dados bancários confirmados e responsável pela logística resolvido.</Nota>
    </>
  );
}

function ModalNovaAvaliacao({ compradores, aoFechar, aoCriar }: { compradores: { g: { id: string; nome: string }; ents: { id: string; razao: string; cnpj: string }[] }[]; aoFechar: () => void; aoCriar: (entidadeId: string) => void }) {
  const [g, setG] = useState(compradores[0]?.g.id ?? '');
  const ents = compradores.find((x) => x.g.id === g)?.ents ?? [];
  const [e, setE] = useState(ents[0]?.id ?? '');
  return (
    <Modal
      titulo="Adicionar avaliação de comprador"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!e} onClick={() => (aoCriar(e), aoFechar())} data-teste="criar-avaliacao">
            Adicionar avaliação
          </button>
        </>
      }
    >
      <label className="campo">
        Parceiro comercial (Comprador de Trade-in)
        <select
          value={g}
          onChange={(ev) => {
            setG(ev.target.value);
            setE(compradores.find((x) => x.g.id === ev.target.value)?.ents[0]?.id ?? '');
          }}
          data-teste="avaliacao-parceiro"
        >
          {compradores.map((x) => (
            <option key={x.g.id} value={x.g.id}>
              {x.g.nome}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        Entidade/CNPJ compradora
        <select value={e} onChange={(ev) => setE(ev.target.value)} data-teste="avaliacao-entidade">
          {ents.map((x) => (
            <option key={x.id} value={x.id}>
              {x.razao} — {x.cnpj}
            </option>
          ))}
        </select>
      </label>
      <p className="texto-secundario">Depois, informe a avaliação por linha em “Valores por linha”, compare e marque uma como Selecionada.</p>
    </Modal>
  );
}

/** Importação com PRÉVIA: nada entra no inventário antes da revisão e da confirmação. A leitura é no próprio navegador, com limites. */
function ModalImportarPlanilha({ tri, eu, aoFechar, aoConfirmar }: { tri: DadosTradein; eu: string; aoFechar: () => void; aoConfirmar: (novos: ComponenteOperacao[], registro: NonNullable<DadosTradein['importacoes']>[number]) => void }) {
  const entrada = useRef<HTMLInputElement>(null);
  const [estado, setEstado] = useState<'inicial' | 'carregando' | 'erro' | 'previa'>('inicial');
  const [erro, setErro] = useState('');
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [arquivo, setArquivo] = useState<{ nome: string; assinatura: string } | null>(null);
  const ler = async (f: File) => {
    setEstado('carregando');
    try {
      if (!/\.xlsx$/i.test(f.name)) throw new Error('Use o modelo .xlsx (outros formatos não são aceitos).');
      if (f.size > LIMITES_INVENTARIO.bytes) throw new Error(`Arquivo grande demais (máximo ${Math.round(LIMITES_INVENTARIO.bytes / 1024)} KB).`);
      const bytes = new Uint8Array(await f.arrayBuffer());
      const ass = assinatura(bytes);
      const ja = (tri.importacoes ?? []).find((i) => i.assinatura === ass);
      if (ja) throw new Error(`Este mesmo arquivo já foi importado em ${dataBr(ja.dataHora.slice(0, 10))} às ${ja.dataHora.slice(11, 16)} por ${ja.usuario} (${ja.arquivo}). Nada foi importado de novo.`);
      const p = validarPlanilhaInventario(await lerXlsx(bytes), tri.equipamentos);
      if (p.erroModelo) throw new Error(p.erroModelo);
      setArquivo({ nome: f.name.slice(0, 200), assinatura: ass });
      setPrevia(p);
      setEstado('previa');
    } catch (e) {
      setErro((e as Error).message);
      setEstado('erro');
    }
  };
  const validas = previa?.linhas.filter((l) => l.status === 'valida') ?? [];
  const confirmar = () => {
    if (!previa || !arquivo) return;
    aoConfirmar(
      validas.map((l) => novoEquipamento({ ...l.equipamento, descricao: '', quantidade: l.equipamento!.quantidade ?? 1, lote: l.equipamento!.serial && /lote/i.test(l.equipamento!.serial) ? l.equipamento!.serial : '' })),
      { arquivo: arquivo.nome, assinatura: arquivo.assinatura, usuario: eu, dataHora: agoraSaoPaulo(), validas: previa.contagem.valida, invalidas: previa.contagem.invalida, duplicadas: previa.contagem.duplicada, ignoradas: previa.contagem.ignorada },
    );
    aoFechar();
  };
  return (
    <Modal
      titulo="Importar planilha de inventário"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={estado !== 'previa' || validas.length === 0 || (previa?.contagem.invalida ?? 0) > 0} onClick={confirmar} data-teste="confirmar-importacao">
            Importar {validas.length} linha(s) válida(s)
          </button>
        </>
      }
    >
      <ol className="passos-importacao">
        <li>O Cliente preenche o modelo (.xlsx).</li>
        <li>Você escolhe o arquivo: o HUB mostra a prévia.</li>
        <li>Linhas válidas, inválidas, duplicadas e ignoradas ficam identificadas.</li>
        <li>Só depois da revisão você confirma — nada é sobrescrito. As linhas entram no rascunho: salve a versão para gravar.</li>
      </ol>
      <label className="campo">
        Arquivo da planilha (.xlsx)
        <input ref={entrada} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => e.target.files?.[0] && void ler(e.target.files[0])} data-teste="arquivo-planilha" />
      </label>
      {estado === 'carregando' && <Estado tipo="carregando" titulo="lendo a planilha no próprio navegador" />}
      {estado === 'erro' && <Estado tipo="erro" titulo={erro} teste="erro-planilha" />}
      {estado === 'previa' && previa && (
        <>
          <Estado tipo={previa.contagem.invalida ? 'bloqueado' : 'concluido'} titulo={`${previa.contagem.valida} válida(s), ${previa.contagem.invalida} inválida(s), ${previa.contagem.duplicada} duplicada(s), ${previa.contagem.ignorada} ignorada(s)`} itens={previa.contagem.invalida ? ['Corrija as linhas inválidas da planilha e importe de novo.'] : undefined} teste="resumo-previa" />
          <div className="rolagem">
            <table className="tabela tabela-compacta" data-teste="previa-planilha">
              <thead>
                <tr>
                  <th>Linha</th>
                  <th>Situação</th>
                  <th>Motivo</th>
                  {COLUNAS_INVENTARIO.slice(0, 5).map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                  <th>Qtd</th>
                </tr>
              </thead>
              <tbody>
                {previa.linhas.map((l) => (
                  <tr key={l.numero} className={l.status === 'invalida' ? 'prioridade-critico' : l.status === 'duplicada' ? 'prioridade-urgente' : ''} data-teste={`previa-${l.numero}`} data-status={l.status}>
                    <td>{l.numero}</td>
                    <td>{l.status === 'valida' ? '✓ válida' : l.status === 'invalida' ? '✖ inválida' : l.status === 'duplicada' ? '▲ duplicada' : '○ ignorada'}</td>
                    <td>{l.motivo}</td>
                    <td>{l.equipamento?.categoria}</td>
                    <td>{l.equipamento?.fabricante}</td>
                    <td>{l.equipamento?.modelo}</td>
                    <td>{l.equipamento?.pn}</td>
                    <td>{l.equipamento?.serial}</td>
                    <td>{l.equipamento?.quantidade ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

/**
 * Dados bancários do favorecido: seguem por canal apropriado FORA do HUB (nada é digitado nem guardado aqui).
 * O HUB registra só a confirmação operacional, com permissão própria e auditoria.
 */
function ConfirmacaoBancariaTradein({ v, editavel, propostaId, alterado }: { v: Versao; editavel: boolean; propostaId: string; alterado?: boolean }) {
  const { aplicar } = useProposta();
  const { pode } = useSessao();
  const [confirmar, setConfirmar] = useState(false);
  const [erro, setErro] = useState('');
  const c = v.tradein!.confirmacaoBancaria;
  const executar = async () => {
    try {
      aplicar(await api.post<DadosProposta>(`/api/propostas/${propostaId}/confirmar-dados-bancarios`, {}));
      setConfirmar(false);
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <section className="secao-editor" data-teste="dados-bancarios">
      <h2>Dados bancários do favorecido</h2>
      <p className="texto-secundario">Onde o comprador depositará o valor destinado ao Cliente. Os dados bancários seguem por canal apropriado, fora do HUB, e não aparecem no PDF. Aqui fica registrada só a confirmação operacional, com auditoria.</p>
      {c ? (
        <p className="aviso aviso-info" data-teste="confirmacao-bancaria">
          ✓ Dados bancários confirmados por <strong>{c.usuario}</strong> em {dataBr(c.dataHora.slice(0, 10))} às {c.dataHora.slice(11, 16)}.
        </p>
      ) : (
        <p className="aviso aviso-alerta" data-teste="confirmacao-bancaria">
          <span className="rotulo-nivel">▲ Pendente</span>Dados bancários ainda não confirmados: a versão não pode ser enviada.
        </p>
      )}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {editavel && !c && pode('confirmar_dados_bancarios', 'proposta') && (
        <button type="button" className="botao-secundario" disabled={alterado} title={alterado ? 'Salve a versão antes de confirmar' : undefined} onClick={() => setConfirmar(true)} data-teste="confirmar-conta">
          Confirmar dados bancários 🔒
        </button>
      )}
      {confirmar && (
        <Modal
          titulo="Confirmar dados bancários (ação auditada)"
          aoFechar={() => setConfirmar(false)}
          rodape={
            <>
              <button type="button" className="botao-secundario" onClick={() => setConfirmar(false)}>
                Cancelar
              </button>
              <button type="button" className="botao-primario" onClick={() => void executar()} data-teste="confirmar-conta-ok">
                Confirmo
              </button>
            </>
          }
        >
          <p>Confirmo que os dados bancários do favorecido (Cliente ou entidade autorizada) foram conferidos pelo canal apropriado, fora do HUB.</p>
          <p className="texto-secundario">Fica registrado com o seu usuário, a data e a hora, na auditoria e na Timeline. Uma nova versão da Proposta exige nova confirmação.</p>
        </Modal>
      )}
    </section>
  );
}
